import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import api from '../../utils/api';

const C = {
  navy: '#0D1B2A', teal: '#00B4D8', tealDark: '#0087A8',
  bg: '#F0F7FA', success: '#10B981', warning: '#F59E0B',
  error: '#EF4444', dark: '#1F2937', grey: '#6B7280',
  border: '#E5E7EB', white: '#FFFFFF',
};

const STATUS = {
  pending:        { label: 'Pending',           color: C.warning, bg: '#FEF3C7', icon: '⏳' },
  proof_uploaded: { label: 'Proof Uploaded',    color: '#3B82F6', bg: '#EFF6FF', icon: '📎' },
  confirmed:      { label: 'Confirmed',         color: C.success, bg: '#D1FAE5', icon: '✅' },
  rejected:       { label: 'Rejected',          color: C.error,   bg: '#FEE2E2', icon: '❌' },
  overdue:        { label: 'Overdue',           color: C.error,   bg: '#FEE2E2', icon: '🚨' },
};

const ACCOUNT_STATUS = {
  active:    { label: 'Active',    color: C.success, bg: '#D1FAE5' },
  on_hold:   { label: 'On Hold',  color: C.error,   bg: '#FEE2E2' },
  suspended: { label: 'Suspended',color: C.error,   bg: '#FEE2E2' },
};

export default function SuperAdminSubscriptions() {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [actionId, setActionId] = useState(null); // { id, type: 'confirm'|'reject' }
  const [notes, setNotes] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [filter, setFilter] = useState('action_needed'); // action_needed | all
  const [mainTab, setMainTab] = useState('subscriptions'); // subscriptions | overdue | addons
  const [overdueData, setOverdueData] = useState(null);
  const [addonModal, setAddonModal] = useState(null); // { paymentId, tenantName, billingMonth, currentAddons, baseAmount }
  const [addonForm, setAddonForm] = useState({ label: '', amount_usd: '' });
  const [addonTypes, setAddonTypes] = useState([]);
  const [addonProcessing, setAddonProcessing] = useState(false);

  const load = async () => {
    try {
      const res = await api.get('/api/superadmin/subscriptions');
      setSubs(res.data.subscriptions || []);
    } catch {
      setError('Failed to load subscriptions.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const loadOverdue = async () => {
    try {
      const [od, at] = await Promise.all([
        api.get('/api/superadmin/subscriptions/overdue-dashboard'),
        api.get('/api/superadmin/subscriptions/addon-types'),
      ]);
      setOverdueData(od.data);
      setAddonTypes(at.data || []);
    } catch {}
  };

  useEffect(() => { if (mainTab === 'overdue' || mainTab === 'addons') loadOverdue(); }, [mainTab]);

  const addAddon = async () => {
    if (!addonModal || !addonForm.label || !addonForm.amount_usd) return;
    setAddonProcessing(true);
    try {
      await api.post(`/api/superadmin/subscriptions/${addonModal.paymentId}/add-addon`, {
        label: addonForm.label, amount_usd: parseFloat(addonForm.amount_usd)
      });
      setSuccess(`Add-on added to ${addonModal.tenantName} invoice`);
      setAddonModal(null); setAddonForm({ label: '', amount_usd: '' });
      load(); loadOverdue();
      setTimeout(() => setSuccess(''), 4000);
    } catch (e) { setError(e?.response?.data?.error || 'Failed'); }
    setAddonProcessing(false);
  };

  const removeAddon = async (paymentId, idx) => {
    if (!window.confirm('Remove this add-on?')) return;
    try {
      await api.post(`/api/superadmin/subscriptions/${paymentId}/remove-addon`, { index: idx });
      load(); loadOverdue();
      setSuccess('Add-on removed');
      setTimeout(() => setSuccess(''), 3000);
    } catch { setError('Failed to remove'); }
  };

  const [previewUrl, setPreviewUrl] = React.useState(null);
  const [previewMeta, setPreviewMeta] = React.useState(null);

  const previewInvoice = async (paymentId) => {
    try {
      const r = await api.get('/api/superadmin/subscriptions/' + paymentId + '/share-url');
      setPreviewUrl(r.data.share_url);
      setPreviewMeta(r.data);
    } catch { setError('Could not load invoice preview'); }
  };

  // Friendly, status-aware share message. includeUrl=false for Telegram
  // (Telegram appends the link itself via the url param).
  const shareText = (meta, url, includeUrl = true) => {
    if (!meta) return includeUrl ? ('Your SpeakIQ invoice — view & download: ' + url) : 'Your SpeakIQ invoice';
    const amt = '$' + (Number(meta.amount) || 0).toFixed(2) + ' USD';
    const who = meta.tenant_name ? (' ' + meta.tenant_name) : ' there';
    const inv = meta.invoice_number ? (' ' + meta.invoice_number) : '';
    const tail = includeUrl ? ('\n\nView & download your invoice here:\n' + url + '\n\nThank you,\nSpeakIQ') : '\n\nThank you,\nSpeakIQ';
    if (meta.status === 'overdue') {
      return 'Hello' + who + ',\n\nThis is a friendly reminder that your SpeakIQ invoice' + inv + ' for ' + meta.billing_month + ' (' + amt + ') is now overdue' + (meta.due_date ? ' — it was due on ' + meta.due_date : '') + '. Please arrange payment at your earliest convenience.' + tail;
    }
    return 'Hello' + who + ',\n\nPlease find your SpeakIQ invoice' + inv + ' for ' + meta.billing_month + ' (' + amt + ').' + tail;
  };
  const shareSubject = (meta) => (meta && meta.status === 'overdue' ? 'Overdue: ' : '') + 'Your SpeakIQ Invoice' + (meta && meta.invoice_number ? ' ' + meta.invoice_number : '');

  // Manual Activate/Disable override — works regardless of overdue invoices.
  const toggleAccount = async (sub) => {
    const isActive = (sub.account_status || 'active') === 'active';
    const msg = isActive
      ? `Disable ${sub.tenant_name}? They will stop accepting new student registrations and bookings.`
      : `Activate ${sub.tenant_name}? This overrides any overdue-invoice hold.`;
    if (!window.confirm(msg)) return;
    try {
      const url = isActive
        ? '/api/superadmin/partners/' + sub.tenant_id + '/suspend'
        : '/api/superadmin/partners/' + sub.tenant_id + '/reinstate';
      await api.post(url, { reason: isActive ? 'Manual disable by superadmin' : 'Manual activation by superadmin', duration_days: null });
      setSuccess(`${sub.tenant_name} ${isActive ? 'disabled' : 'activated'}`);
      load();
      setTimeout(() => setSuccess(''), 4000);
    } catch (e) { setError(e?.response?.data?.error || 'Failed to update account status'); }
  };

  const confirmDirect = async (paymentId, tenantName, billingMonth) => {
    const notes = window.prompt('Confirm payment for ' + tenantName + ' — ' + billingMonth + '? Add a note (optional):', '');
    if (notes === null) return;
    try {
      const r = await api.post('/api/superadmin/subscriptions/' + paymentId + '/confirm-direct', { notes });
      setSuccess(r.data.message);
      load(); loadOverdue();
      setTimeout(() => setSuccess(''), 5000);
    } catch (e) { setError(e && e.response && e.response.data && e.response.data.error ? e.response.data.error : 'Failed to confirm'); }
  };

  const unconfirmPayment = async (paymentId, tenantName, billingMonth) => {
    if (!window.confirm('Mark ' + tenantName + ' — ' + billingMonth + ' as UNPAID?\n\nThis reverses the confirmation (sets it back to awaiting payment). The partner is NOT notified.')) return;
    try {
      const r = await api.post('/api/superadmin/subscriptions/' + paymentId + '/unconfirm', {});
      setSuccess(r.data.message);
      load(); loadOverdue();
      setTimeout(() => setSuccess(''), 5000);
    } catch (e) { setError(e && e.response && e.response.data && e.response.data.error ? e.response.data.error : 'Failed to mark unpaid'); }
  };

  const downloadInvoice = (paymentId, label) => {
    const token = localStorage.getItem('speakiq_token') || '';
    fetch('/api/superadmin/subscriptions/' + paymentId + '/invoice', {
      headers: { 'Authorization': 'Bearer ' + token }
    }).then(function(r) { return r.blob(); }).then(function(blob) {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'SpeakIQ-Invoice-' + (label || paymentId) + '.pdf';
      document.body.appendChild(a); a.click(); setTimeout(function() { document.body.removeChild(a); URL.revokeObjectURL(url); }, 2000);
    }).catch(function() { alert('Failed to download invoice'); });
  };

  const addApiCosts = async (paymentId, tenantName) => {
    try {
      const r = await api.post(`/api/superadmin/subscriptions/${paymentId}/add-api-costs`);
      setSuccess(r.data.message);
      load(); loadOverdue();
      setTimeout(() => setSuccess(''), 4000);
    } catch (e) { setError(e?.response?.data?.error || 'Failed'); }
  };

  const handleConfirm = async (paymentId) => {
    setProcessing(true);
    try {
      await api.post(`/api/superadmin/subscriptions/${paymentId}/confirm`, { notes });
      setSuccess('Payment confirmed! Partner has been notified.');
      setActionId(null);
      setNotes('');
      await load();
      setTimeout(() => setSuccess(''), 5000);
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to confirm payment.');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async (paymentId) => {
    if (!rejectReason.trim()) { setError('Please enter a rejection reason.'); return; }
    setProcessing(true);
    try {
      await api.post(`/api/superadmin/subscriptions/${paymentId}/reject`, { reason: rejectReason });
      setSuccess('Proof rejected. Partner has been asked to re-submit.');
      setActionId(null);
      setRejectReason('');
      await load();
      setTimeout(() => setSuccess(''), 5000);
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to reject payment.');
    } finally {
      setProcessing(false);
    }
  };

  const handleTriggerCheck = async () => {
    try {
      const res = await api.post('/api/superadmin/subscriptions/check-overdue');
      setSuccess(`Overdue check run: ${res.data.suspended || 0} suspended, ${res.data.reminder_1 + res.data.reminder_3 + res.data.reminder_7} reminders sent.`);
      await load();
      setTimeout(() => setSuccess(''), 6000);
    } catch {
      setError('Failed to run overdue check.');
    }
  };

  const displayed = filter === 'action_needed'
    ? subs.filter(s => ['proof_uploaded', 'overdue', 'rejected', 'pending'].includes(s.current_payment?.status))
    : subs;

  const MAIN_TABS = [
    { id: 'subscriptions', label: '💳 Subscriptions' },
    { id: 'overdue', label: overdueData ? `🚨 Overdue ($${overdueData.total_outstanding_usd?.toFixed(0)})` : '🚨 Overdue Dashboard' },
    { id: 'addons', label: '➕ Add-ons & Extras' },
  ];

  const proofCount = subs.filter(s => s.current_payment?.status === 'proof_uploaded').length;
  const overdueCount = subs.reduce((sum, s) => sum + (s.total_overdue_count || (s.days_overdue > 0 && s.current_payment?.status !== 'confirmed' ? 1 : 0)), 0);
  const totalMRR = subs.reduce((sum, s) => sum + (s.current_payment?.amount_usd || 0), 0);
  const confirmedMRR = subs.filter(s => s.current_payment?.status === 'confirmed').reduce((sum, s) => sum + (s.current_payment?.amount_usd || 0), 0);

  return (
    <Layout role="superadmin">
      <div style={{ padding: '32px 24px', maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h1 style={{ fontSize: 26, fontWeight: 700, color: C.dark, margin: 0 }}>Partner Subscriptions</h1>
            <p style={{ color: C.grey, margin: '4px 0 0', fontSize: 14 }}>Review payment proofs and confirm monthly subscriptions</p>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <button onClick={handleTriggerCheck} style={{ padding: '8px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600, background: '#FEF3C7', color: '#92400E', border: '1px solid #FCD34D', cursor: 'pointer' }}>
              ⏰ Run Overdue Check
            </button>
            {['action_needed', 'all'].map(f => (
              <button key={f} onClick={() => setFilter(f)} style={{
                padding: '8px 16px', borderRadius: 8, fontWeight: 600, fontSize: 13,
                border: `2px solid ${filter === f ? C.teal : C.border}`,
                background: filter === f ? C.teal : C.white,
                color: filter === f ? C.white : C.dark, cursor: 'pointer',
              }}>
                {f === 'action_needed' ? `Needs Action (${proofCount + overdueCount})` : 'All Partners'}
              </button>
            ))}
          </div>
        </div>

        {/* Summary */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 28 }}>
          {[
            { label: 'Proof to Review', value: proofCount, color: '#3B82F6', icon: '📎' },
            { label: 'Overdue', value: overdueCount, color: C.error, icon: '🚨' },
            { label: 'Total MRR', value: `$${totalMRR.toFixed(0)}`, color: C.teal, icon: '💰' },
            { label: 'Collected', value: `$${confirmedMRR.toFixed(0)}`, color: C.success, icon: '✅' },
          ].map(card => (
            <div key={card.label} style={{ background: C.white, borderRadius: 12, padding: '18px 20px', border: `1px solid ${C.border}`, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
              <div style={{ fontSize: 20, marginBottom: 4 }}>{card.icon}</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: card.color }}>{card.value}</div>
              <div style={{ fontSize: 12, color: C.grey, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{card.label}</div>
            </div>
          ))}
        </div>

        {success && <div style={{ background: '#D1FAE5', border: '1px solid #6EE7B7', color: '#065F46', borderRadius: 8, padding: '12px 16px', marginBottom: 20, fontWeight: 500 }}>✓ {success}</div>}
        {error && <div style={{ background: '#FEE2E2', border: '1px solid #FCA5A5', color: '#991B1B', borderRadius: 8, padding: '12px 16px', marginBottom: 20, fontWeight: 500 }}>✗ {error}</div>}

        {loading ? (
          <div style={{ textAlign: 'center', padding: 60, color: C.grey }}>Loading…</div>
        ) : displayed.length === 0 ? (
          <div style={{ background: C.white, borderRadius: 16, padding: 60, textAlign: 'center', border: `1px solid ${C.border}` }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
            <h3 style={{ margin: 0, color: C.dark }}>All caught up!</h3>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {displayed.map(sub => {
              const p = sub.current_payment;
              const pStatus = STATUS[p?.status] || STATUS.pending;
              const acctStatus = ACCOUNT_STATUS[sub.account_status] || ACCOUNT_STATUS.active;
              const isExpanded = expandedId === sub.tenant_id;
              const isActioning = actionId?.id === p?.id;

              return (
                <div key={sub.tenant_id} style={{ background: C.white, borderRadius: 14, border: `1px solid ${C.border}`, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
                  {/* Header row */}
                  <div style={{ padding: '18px 22px', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                    <div style={{ width: 44, height: 44, borderRadius: '50%', background: `linear-gradient(135deg, ${C.teal}, ${C.tealDark})`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.white, fontWeight: 700, fontSize: 16, flexShrink: 0 }}>
                      {(sub.tenant_name?.[0] || '?').toUpperCase()}
                    </div>
                    <div style={{ flex: 1, minWidth: 140 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 700, fontSize: 15, color: C.dark }}>{sub.tenant_name}</span>
                        <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: acctStatus.bg, color: acctStatus.color, fontWeight: 600 }}>{acctStatus.label}</span>
                        <button onClick={() => toggleAccount(sub)}
                          title="Manually activate or disable this partner (overrides overdue hold)"
                          style={{ fontSize: 11, padding: '2px 10px', borderRadius: 20, border: 'none', cursor: 'pointer', fontWeight: 700,
                            background: (sub.account_status || 'active') === 'active' ? '#FEE2E2' : '#D1FAE5',
                            color: (sub.account_status || 'active') === 'active' ? C.error : C.success }}>
                          {(sub.account_status || 'active') === 'active' ? 'Disable' : 'Activate'}
                        </button>
                        {p?.is_plan_change && <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: '#FFF7ED', color: '#92400E', fontWeight: 700, border: '1px solid #FED7AA' }}>🔄 PLAN CHANGE → {p.new_plan}</span>}
                      </div>
                      <div style={{ fontSize: 12, color: C.grey }}>{sub.admin_email} · {sub.plan}{p?.is_plan_change && p?.new_plan ? ` → ${p.new_plan}` : ''}</div>
                    </div>

                    <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 11, color: C.grey, fontWeight: 600, textTransform: 'uppercase' }}>Amount</div>
                        <div style={{ fontSize: 18, fontWeight: 700, color: C.teal }}>${p?.amount_usd?.toFixed(2)}</div>
                      </div>
                      {(sub.total_overdue_count > 0 || sub.days_overdue > 0) && p?.status !== 'confirmed' && (
                        <div style={{ background: '#FEE2E2', borderRadius: 8, padding: '4px 10px', fontSize: 12, fontWeight: 700, color: C.error }}>
                          {sub.total_overdue_count > 0 ? sub.total_overdue_count + (sub.total_overdue_count > 1 ? " invoices overdue" : " invoice overdue") : sub.days_overdue + "d overdue"}
                        </div>
                      )}
                      <span style={{ fontSize: 12, fontWeight: 700, padding: '4px 10px', borderRadius: 20, background: pStatus.bg, color: pStatus.color }}>
                        {pStatus.icon} {pStatus.label}
                      </span>
                    </div>

                    <div style={{ display: 'flex', gap: 8 }}>
                      {(p?.status === 'proof_uploaded' || (p?.status === 'pending' && p?.is_plan_change)) && !isActioning && (
                        <>
                          <button onClick={() => { setActionId({ id: p.id, type: 'confirm' }); setNotes(''); setError(''); }}
                            style={{ padding: '7px 14px', borderRadius: 8, fontWeight: 700, fontSize: 13, background: p?.is_plan_change ? '#D97706' : C.success, color: C.white, border: 'none', cursor: 'pointer' }}>
                            {p?.is_plan_change ? '🔄 Approve Change' : '✓ Confirm'}
                          </button>
                          <button onClick={() => { setActionId({ id: p.id, type: 'reject' }); setRejectReason(''); setError(''); }}
                            style={{ padding: '7px 14px', borderRadius: 8, fontWeight: 700, fontSize: 13, background: C.error, color: C.white, border: 'none', cursor: 'pointer' }}>
                            ✗ Reject
                          </button>
                        </>
                      )}
                      <button onClick={() => setExpandedId(isExpanded ? null : sub.tenant_id)}
                        style={{ padding: '7px 12px', borderRadius: 8, fontSize: 13, background: '#F3F4F6', color: C.dark, border: 'none', cursor: 'pointer', fontWeight: 600 }}>
                        {isExpanded ? '▲' : '▼'}
                      </button>
                    </div>
                  </div>

                  {/* Action panel */}
                  {isActioning && (
                    <div style={{ borderTop: `2px solid ${actionId.type === 'confirm' ? C.success : C.error}`, padding: '16px 22px', background: actionId.type === 'confirm' ? '#F0FDF4' : '#FFF5F5' }}>
                      <div style={{ fontWeight: 700, fontSize: 14, color: C.dark, marginBottom: 12 }}>
                        {actionId.type === 'confirm' ? '✓ Confirm Payment' : '✗ Reject Proof'} — {sub.tenant_name}
                      </div>
                      {p?.proof_url && (
                        <div style={{ marginBottom: 12 }}>
                          <a href={p.proof_url} target="_blank" rel="noreferrer"
                            style={{ fontSize: 13, color: '#3B82F6', fontWeight: 600, textDecoration: 'underline' }}>
                            📎 View Payment Proof
                          </a>
                        </div>
                      )}
                      <div style={{ marginBottom: 12 }}>
                        <label style={{ fontSize: 13, fontWeight: 600, color: C.dark, display: 'block', marginBottom: 4 }}>
                          {actionId.type === 'confirm' ? 'Notes (optional)' : 'Rejection Reason *'}
                        </label>
                        <input
                          value={actionId.type === 'confirm' ? notes : rejectReason}
                          onChange={e => actionId.type === 'confirm' ? setNotes(e.target.value) : setRejectReason(e.target.value)}
                          placeholder={actionId.type === 'confirm' ? 'Any notes…' : 'e.g. Screenshot unclear, wrong amount…'}
                          style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 14, boxSizing: 'border-box', outline: 'none' }}
                        />
                      </div>
                      <div style={{ display: 'flex', gap: 10 }}>
                        <button onClick={() => actionId.type === 'confirm' ? handleConfirm(p.id) : handleReject(p.id)}
                          disabled={processing}
                          style={{ padding: '9px 20px', borderRadius: 8, fontWeight: 700, fontSize: 14, background: processing ? C.grey : (actionId.type === 'confirm' ? C.success : C.error), color: C.white, border: 'none', cursor: processing ? 'not-allowed' : 'pointer' }}>
                          {processing ? 'Processing…' : (actionId.type === 'confirm' ? '✓ Confirm Payment' : '✗ Reject Proof')}
                        </button>
                        <button onClick={() => { setActionId(null); setError(''); }}
                          style={{ padding: '9px 16px', borderRadius: 8, fontSize: 14, background: C.white, color: C.dark, border: `1px solid ${C.border}`, cursor: 'pointer', fontWeight: 600 }}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Expanded history */}
                  {isExpanded && (
                    <div style={{ borderTop: `1px solid ${C.border}`, padding: '16px 22px', background: '#FAFAFA' }}>
                      <h4 style={{ margin: '0 0 12px', fontSize: 13, fontWeight: 700, color: C.dark }}>Payment History</h4>
                      {sub.history.length === 0 ? (
                        <p style={{ color: C.grey, fontSize: 13, margin: 0 }}>No history yet.</p>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {sub.history.map(h => {
                            const hs = STATUS[h.status] || STATUS.pending;
                            return (
                              <div key={h.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: C.white, borderRadius: 8, fontSize: 13, border: `1px solid ${C.border}` }}>
                                <span style={{ fontWeight: 600, color: C.dark }}>{h.billing_month}</span>
                                <span style={{ color: C.teal, fontWeight: 700 }}>${h.amount_usd?.toFixed(2)}</span>
                                <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: hs.bg, color: hs.color, fontWeight: 700 }}>{hs.icon} {hs.label}</span>
                                {h.confirmed_at && <span style={{ color: C.grey, fontSize: 11 }}>{new Date(h.confirmed_at).toLocaleDateString('en-GB')}</span>}
                            <button onClick={() => previewInvoice(h.id)}
                              style={{ padding: '3px 8px', borderRadius: 5, background: '#F0F7FA', color: C.navy, border: '1px solid #E5E7EB', cursor: 'pointer', fontSize: 11, fontWeight: 600, marginLeft: 4 }}>
                              👁
                            </button>
                            <button onClick={() => downloadInvoice(h.id, h.billing_month)}
                              style={{ padding: '3px 8px', borderRadius: 5, background: '#00B4D8', color: 'white', border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 600, marginLeft: 2 }}>
                              ⬇
                            </button>
                            {h.status !== 'confirmed' && (
                              <button onClick={() => confirmDirect(h.id, sub.tenant_name, h.billing_month)}
                                style={{ padding: '3px 10px', borderRadius: 5, background: '#D1FAE5', color: '#065F46', border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 600, marginLeft: 4 }}>
                                ✓ Mark Paid
                              </button>
                            )}
                            {h.status === 'confirmed' && (
                              <button onClick={() => unconfirmPayment(h.id, sub.tenant_name, h.billing_month)}
                                style={{ padding: '3px 10px', borderRadius: 5, background: '#FEE2E2', color: '#991B1B', border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 600, marginLeft: 4 }}>
                                ↩ Mark Unpaid
                              </button>
                            )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── MAIN TAB SWITCHER ── */}
      <div style={{ padding: '0 0 0 0', maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 20, paddingLeft: 'clamp(16px,4vw,32px)' }}>
          {MAIN_TABS.map(t => (
            <button key={t.id} onClick={() => setMainTab(t.id)} style={{
              padding: '9px 20px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
              background: mainTab === t.id ? C.teal : '#fff', color: mainTab === t.id ? '#fff' : C.navy,
              border: `1px solid ${mainTab === t.id ? C.teal : C.border}`,
            }}>{t.label}</button>
          ))}
        </div>
      </div>

      {/* ── OVERDUE DASHBOARD TAB ── */}
      {mainTab === 'overdue' && (
        <div style={{ padding: '0 clamp(16px,4vw,32px) 40px', maxWidth: 1100, margin: '0 auto' }}>
          {!overdueData ? (
            <div style={{ textAlign: 'center', padding: 60, color: C.grey }}>Loading...</div>
          ) : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: 16, marginBottom: 24 }}>
                {[
                  { label: 'Total Outstanding', value: `$${overdueData.total_outstanding_usd?.toFixed(2)}`, color: C.error, icon: '💰' },
                  { label: 'Overdue Invoices', value: overdueData.total_overdue_invoices, color: C.error, icon: '🚨' },
                  { label: 'Partners Affected', value: overdueData.tenants?.length || 0, color: C.warning, icon: '🏫' },
                ].map(s => (
                  <div key={s.label} style={{ background: '#fff', borderRadius: 12, border: `1px solid ${C.border}`, padding: '18px 20px' }}>
                    <div style={{ fontSize: 24 }}>{s.icon}</div>
                    <div style={{ fontSize: 28, fontWeight: 800, color: s.color }}>{s.value}</div>
                    <div style={{ fontSize: 12, color: C.grey, marginTop: 4 }}>{s.label}</div>
                  </div>
                ))}
              </div>

              {overdueData.tenants?.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 40, background: '#D1FAE5', borderRadius: 14, color: '#065F46', fontSize: 16, fontWeight: 700 }}>
                  ✅ No outstanding payments across all partners!
                </div>
              ) : overdueData.tenants.map(t => (
                <div key={t.tenant_id} style={{ background: '#fff', borderRadius: 14, border: `2px solid ${t.max_days_overdue > 7 ? '#FCA5A5' : C.border}`, marginBottom: 16, overflow: 'hidden' }}>
                  <div style={{ padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: t.max_days_overdue > 7 ? '#FFF5F5' : C.bg, flexWrap: 'wrap', gap: 12 }}>
                    <div>
                      <span style={{ fontWeight: 800, fontSize: 16, color: C.navy }}>{t.tenant_name}</span>
                      <span style={{ marginLeft: 12, fontSize: 12, color: C.grey }}>{t.admin_email}</span>
                      {t.max_days_overdue > 0 && <span style={{ marginLeft: 12, fontSize: 12, fontWeight: 700, color: C.error }}>Max {t.max_days_overdue}d overdue</span>}
                    </div>
                    <div style={{ fontWeight: 800, fontSize: 20, color: C.error }}>${t.total_outstanding?.toFixed(2)} outstanding</div>
                  </div>
                  <div style={{ padding: '12px 20px' }}>
                    {t.payments.map(p => {
                      const ps = STATUS[p.status] || STATUS.pending;
                      return (
                        <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', marginBottom: 8, background: '#F9FAFB', borderRadius: 10, flexWrap: 'wrap', gap: 8 }}>
                          <div>
                            <span style={{ fontWeight: 700, color: C.navy, fontSize: 14 }}>{p.billing_month}</span>
                            {p.days_late > 0 && <span style={{ marginLeft: 8, fontSize: 11, color: C.error, fontWeight: 700 }}>{p.days_late}d late</span>}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <span style={{ fontWeight: 700, color: C.teal }}>${(p.total_amount_usd || p.amount_usd || 0).toFixed(2)}</span>
                            {(p.addon_charges || []).length > 0 && <span style={{ fontSize: 11, color: C.grey }}>+{(p.addon_charges||[]).length} add-ons</span>}
                            <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: ps.bg, color: ps.color, fontWeight: 700 }}>{ps.icon} {ps.label}</span>
                            <button onClick={() => setAddonModal({ paymentId: p.id, tenantName: t.tenant_name, billingMonth: p.billing_month, currentAddons: p.addon_charges || [], baseAmount: p.amount_usd })}
                              style={{ padding: '5px 12px', borderRadius: 6, background: C.teal, color: '#fff', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                              + Add-on
                            </button>
                            {p.status !== 'confirmed' && (
                              <button onClick={() => confirmDirect(p.id, t.tenant_name, p.billing_month)}
                                style={{ padding: '5px 12px', borderRadius: 6, background: '#10B981', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                                ✓ Confirm Paid
                              </button>
                            )}
                            {p.status === 'confirmed' && (
                              <button onClick={() => unconfirmPayment(p.id, t.tenant_name, p.billing_month)}
                                style={{ padding: '5px 12px', borderRadius: 6, background: '#FEE2E2', color: '#991B1B', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                                ↩ Mark Unpaid
                              </button>
                            )}
                            <button onClick={() => previewInvoice(p.id)}
                              style={{ padding: '5px 12px', borderRadius: 6, background: '#F3F4F6', color: '#0D1B2A', border: '1px solid #E5E7EB', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                              👁 Preview
                            </button>
                            <button onClick={() => downloadInvoice(p.id, p.billing_month)}
                              style={{ padding: '5px 10px', borderRadius: 6, background: '#00B4D8', color: 'white', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                              ⬇
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      )}

      {/* ── ADD-ONS TAB ── */}
      {mainTab === 'addons' && (
        <div style={{ padding: '0 clamp(16px,4vw,32px) 40px', maxWidth: 900, margin: '0 auto' }}>
          <h3 style={{ color: C.navy, marginBottom: 8 }}>Add-on & Extra Charges</h3>
          <p style={{ color: C.grey, fontSize: 13, marginBottom: 24 }}>
            Add extra charges to any tenant's current invoice — Claude API recovery, mock test fees, custom services. The tenant will see the breakdown when paying.
          </p>
          <div style={{ background: '#fff', borderRadius: 12, border: `1px solid ${C.border}`, padding: 24, marginBottom: 24 }}>
            <h4 style={{ margin: '0 0 12px', color: C.navy }}>Standard Add-on Types</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 12 }}>
              {addonTypes.map(at => (
                <div key={at.id} style={{ padding: 14, borderRadius: 10, border: `1px solid ${C.border}`, background: C.bg }}>
                  <div style={{ fontWeight: 700, color: C.navy, fontSize: 13 }}>{at.name}</div>
                  <div style={{ fontSize: 12, color: C.grey, marginTop: 4 }}>{at.description}</div>
                  {at.default_amount_usd > 0 && <div style={{ fontWeight: 700, color: C.teal, marginTop: 6 }}>${at.default_amount_usd}/mo</div>}
                </div>
              ))}
            </div>
          </div>
          <div style={{ background: '#fff', borderRadius: 12, border: `1px solid ${C.border}`, padding: 24 }}>
            <h4 style={{ margin: '0 0 16px', color: C.navy }}>Add to Tenant Invoice</h4>
            <p style={{ color: C.grey, fontSize: 13, marginBottom: 20 }}>
              Select a partner and their current invoice, then add the charge. Go to <button onClick={() => setMainTab('overdue')} style={{ background: 'none', border: 'none', color: C.teal, cursor: 'pointer', fontWeight: 700, padding: 0, fontSize: 13 }}>Overdue Dashboard</button> to add charges per-invoice.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {subs.map(s => s.current_payment && (
                <div key={s.tenant_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderRadius: 10, border: `1px solid ${C.border}`, background: '#F9FAFB', flexWrap: 'wrap', gap: 10 }}>
                  <div>
                    <span style={{ fontWeight: 700, color: C.navy }}>{s.tenant_name}</span>
                    <span style={{ marginLeft: 10, fontSize: 12, color: C.grey }}>{s.current_payment?.billing_month}</span>
                    {(s.current_payment?.addon_charges || []).length > 0 && (
                      <div style={{ fontSize: 11, color: C.grey, marginTop: 4 }}>
                        Add-ons: {(s.current_payment.addon_charges||[]).map(a => `${a.label}: $${a.amount_usd}`).join(' · ')}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => addApiCosts(s.current_payment.id, s.tenant_name)}
                      style={{ padding: '7px 14px', borderRadius: 8, background: '#F3F0FF', color: '#7C3AED', border: '1px solid #DDD6FE', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                      🤖 Add API Costs
                    </button>
                    <button onClick={() => setAddonModal({ paymentId: s.current_payment.id, tenantName: s.tenant_name, billingMonth: s.current_payment.billing_month, currentAddons: s.current_payment.addon_charges || [], baseAmount: s.current_payment.amount_usd })}
                      style={{ padding: '7px 14px', borderRadius: 8, background: C.teal, color: '#fff', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                      + Custom Charge
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── ADD-ON MODAL ── */}
      {addonModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999 }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 32, width: 480, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <h3 style={{ margin: '0 0 4px', color: C.navy }}>Add Charge to Invoice</h3>
            <p style={{ color: C.grey, fontSize: 13, marginBottom: 20 }}>{addonModal.tenantName} — {addonModal.billingMonth} (base: ${addonModal.baseAmount?.toFixed(2)})</p>

            {/* Quick fill from standard types */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: C.grey, marginBottom: 8 }}>Quick fill from standard types:</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {addonTypes.map(at => (
                  <button key={at.id} onClick={() => setAddonForm({ label: at.name, amount_usd: at.default_amount_usd || '' })}
                    style={{ padding: '5px 12px', borderRadius: 6, border: `1px solid ${C.border}`, background: C.bg, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                    {at.name}{at.default_amount_usd > 0 ? ` ($${at.default_amount_usd})` : ''}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: C.navy, display: 'block', marginBottom: 6 }}>Charge Label *</label>
              <input value={addonForm.label} onChange={e => setAddonForm(f => ({...f, label: e.target.value}))} placeholder="e.g. Mock Test Module — June 2026"
                style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 14, boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: C.navy, display: 'block', marginBottom: 6 }}>Amount (USD) *</label>
              <input type="number" min="0.01" step="0.01" value={addonForm.amount_usd} onChange={e => setAddonForm(f => ({...f, amount_usd: e.target.value}))} placeholder="35.00"
                style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 14, boxSizing: 'border-box' }} />
            </div>

            {/* Existing add-ons */}
            {addonModal.currentAddons.length > 0 && (
              <div style={{ marginBottom: 20, padding: 14, background: C.bg, borderRadius: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: C.grey, marginBottom: 8 }}>Existing add-ons on this invoice:</div>
                {addonModal.currentAddons.map((a, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #E5E7EB', fontSize: 13 }}>
                    <span style={{ color: C.navy }}>{a.label}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontWeight: 700, color: C.teal }}>${a.amount_usd?.toFixed(2)}</span>
                      <button onClick={() => removeAddon(addonModal.paymentId, i)}
                        style={{ background: '#FEE2E2', color: C.error, border: 'none', borderRadius: 4, cursor: 'pointer', padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>✕</button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button onClick={() => { setAddonModal(null); setAddonForm({ label: '', amount_usd: '' }); }}
                style={{ padding: '10px 20px', borderRadius: 8, border: `1px solid ${C.border}`, background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Cancel</button>
              <button onClick={addAddon} disabled={addonProcessing || !addonForm.label || !addonForm.amount_usd}
                style={{ padding: '10px 24px', borderRadius: 8, border: 'none', background: C.teal, color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, opacity: addonProcessing ? 0.7 : 1 }}>
                {addonProcessing ? 'Adding...' : 'Add Charge'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Invoice Preview Modal */}
      {previewUrl && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', flexDirection: 'column', zIndex: 9999 }}>
          <div style={{ background: '#0D1B2A', padding: '12px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0, gap: 10, flexWrap: 'wrap' }}>
            <span style={{ color: '#00B4D8', fontWeight: 800, fontSize: 16 }}>Invoice Preview</span>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <a href={previewUrl} target="_blank" rel="noreferrer" style={{ padding: '8px 14px', borderRadius: 8, background: '#00B4D8', color: 'white', textDecoration: 'none', fontSize: 13, fontWeight: 700 }}>↗ Open Full Page</a>
              <a href={previewUrl + '?fmt=pdf&dl=1'} download style={{ padding: '8px 14px', borderRadius: 8, background: '#10B981', color: 'white', textDecoration: 'none', fontSize: 13, fontWeight: 700 }}>⬇ Download PDF</a>
              <a href={'https://wa.me/?text=' + encodeURIComponent(shareText(previewMeta, previewUrl))} target="_blank" rel="noreferrer" style={{ padding: '8px 14px', borderRadius: 8, background: '#25D366', color: 'white', textDecoration: 'none', fontSize: 13, fontWeight: 700 }}>💬 WhatsApp</a>
              <a href={'https://t.me/share/url?url=' + encodeURIComponent(previewUrl) + '&text=' + encodeURIComponent(shareText(previewMeta, previewUrl, false))} target="_blank" rel="noreferrer" style={{ padding: '8px 14px', borderRadius: 8, background: '#229ED9', color: 'white', textDecoration: 'none', fontSize: 13, fontWeight: 700 }}>✈ Telegram</a>
              <a href={'mailto:?subject=' + encodeURIComponent(shareSubject(previewMeta)) + '&body=' + encodeURIComponent(shareText(previewMeta, previewUrl))} style={{ padding: '8px 14px', borderRadius: 8, background: '#6B7280', color: 'white', textDecoration: 'none', fontSize: 13, fontWeight: 700 }}>✉ Email</a>
              <button onClick={(e) => { navigator.clipboard.writeText(shareText(previewMeta, previewUrl)); const b = e.currentTarget; b.textContent = '✓ Copied!'; setTimeout(() => { b.textContent = '🔗 Copy Message'; }, 2000); }} style={{ padding: '8px 14px', borderRadius: 8, background: '#374151', color: 'white', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>🔗 Copy Message</button>
              <button onClick={() => { setPreviewUrl(null); setPreviewMeta(null); }} style={{ padding: '8px 14px', borderRadius: 8, background: '#6B7280', color: 'white', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>✕ Close</button>
            </div>
          </div>
          <iframe src={previewUrl + '?embed=1'} style={{ flex: 1, border: 'none', width: '100%' }} title="Invoice Preview" />
        </div>
      )}
    </Layout>
  );
}
